// 82ZONE hero: sliding carousel (infinite loop), drag/swipe that follows the pointer, autoplay on a fixed timer with progress lines. No dependencies.
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.hero-slideshow__slide')];
    this.lines = [...this.querySelectorAll('.hero-slideshow__line')];
    this.index = 0;
    if (this.slides.length < 2) return;

    this.interval = Number(this.dataset.interval) || 5000;
    this.style.setProperty('--hero-slideshow-interval', `${this.interval}ms`);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayAllowed = this.dataset.autoplay === 'true' && !this.reducedMotion;
    // Phones (under 750px) never autoplay: slides change only when the visitor swipes. Desktop keeps the timer.
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.track = this.querySelector('.hero-slideshow__track');
    this.applyMode(true);
    this.positions = this.slides.map((_, i) => (i === 0 ? 0 : 1));
    // Put every slide in its place now (the last one to the left), so the very first drag pulls in the right neighbour.
    this.layout(true);
    // Bar mode: the bar is split into one segment per slide. Only the showing slide's segment fills; the one just left drains away.
    this.barMode = this.classList.contains('hero-slideshow--bar');

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.lines.forEach((line) =>
      line.addEventListener('click', () => this.go(Number(line.dataset.go)), { signal })
    );

    // Rotating or resizing across 750px switches between swipe-only and autoplay.
    this.mobileQuery.addEventListener('change', () => this.applyMode(), { signal });

    // Screen-reader step buttons (visually hidden, phones only): the progress controls are hidden there, so these keep every slide reachable.
    this.querySelectorAll('.hero-slideshow__step').forEach((button) =>
      button.addEventListener('click', () => this.go(this.index + Number(button.dataset.step)), { signal })
    );

    // The fill animation drives timing, so pause/resume stays in sync with the line.
    this.addEventListener('animationend', (event) => {
      if (!this.barMode && event.animationName === 'hero-slideshow-fill') this.go(this.index + 1);
    }, { signal });

    // Drag (mouse / pen) and swipe (touch): the slides follow the pointer, then snap to the next or previous slide.
    let startX = 0;
    let dragging = false;
    let dragged = false;
    this.addEventListener('pointerdown', (event) => {
      // A touch swipe is not followed by a click, so clear the flag on every new press or the next tap would be swallowed.
      dragged = false;
      if (event.button !== 0 || event.target.closest('button')) return;
      startX = event.clientX;
      dragging = true;
    }, { signal });
    this.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (!dragged && Math.abs(delta) > 6) {
        dragged = true;
        this.toggleAttribute('data-dragging', true);
        this.hold(true);
        // Capture only once it is a real drag, so plain clicks still reach the link.
        if (event.pointerType !== 'touch') this.setPointerCapture(event.pointerId);
      }
      if (dragged) {
        // With two slides the other one must sit on the side the pointer is pulling from.
        if (this.slides.length === 2) this.place(1 - this.index, delta > 0 ? -1 : 1, true);
        this.style.setProperty('--drag', `${delta}px`);
      }
    }, { signal });
    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
      const delta = event.clientX - startX;
      const threshold = Math.max(50, this.clientWidth * 0.15);
      this.removeAttribute('data-dragging');
      this.hold(false);
      this.style.setProperty('--drag', '0px');
      if (dragged && Math.abs(delta) > threshold) this.go(this.index + (delta < 0 ? 1 : -1));
      else if (dragged) this.layout();
    };
    this.addEventListener('pointerup', endDrag, { signal });
    this.addEventListener('pointercancel', endDrag, { signal });
    // A drag must not follow the slide link, and images must not start a native drag.
    this.addEventListener('click', (event) => {
      if (dragged) {
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      }
    }, { signal, capture: true });
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    document.addEventListener('visibilitychange', () => this.hold(document.hidden), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      const i = this.slides.findIndex((slide) => slide.contains(event.target));
      if (i > -1) {
        this.go(i);
        this.hold(true); // theme editor only: keep the selected slide in view while it is edited
      }
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => this.hold(false), { signal });

    if (this.barMode) {
      // Draining a segment uses the slide transition's own duration and easing (.hero-slideshow__slide), so both land together.
      this.glideMs = this.reducedMotion ? 0 : 600;
      this.glideEase = 'cubic-bezier(0.32, 0.72, 0, 1)';
      this.fills = this.lines.map((line) => line.querySelector('.hero-slideshow__fill'));
      this.anims = this.fills.map(() => null);
      this.fillIn(0, true);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.anims?.forEach((_, i) => this.clearFill(i));
  }

  // Autoplay runs only when the setting is on, motion is not reduced and the viewport is 750px or wider.
  applyMode(initial = false) {
    const autoplay = this.autoplayAllowed && !this.mobileQuery.matches;
    if (!initial && autoplay === this.autoplay) return;
    this.autoplay = autoplay;
    // data-static stops the CSS fill animation (lines mode) and shows the active line full.
    this.toggleAttribute('data-static', !autoplay);
    // Without a timer the slide only changes on a user action, so announcing the change politely is safe.
    this.track?.setAttribute('aria-live', autoplay ? 'off' : 'polite');
    // Bar mode: restart the active segment as a running sweep (desktop) or a plain filled one (phone). Not on first load: the end of connectedCallback does it.
    if (this.barMode && !initial) this.fillIn(this.index, true);
  }

  // `auto` is set when the timer advances the slide; a click or swipe moves the bar without any leaving animation.
  go(next, auto = false) {
    const count = this.slides.length;
    const previous = this.index;
    this.index = (next + count) % count;
    this.layout();
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });
    this.lines.forEach((line, i) => {
      line.removeAttribute('aria-current');
      if (i === this.index) {
        // Restart the fill animation on the newly active line.
        void line.offsetWidth;
        line.setAttribute('aria-current', 'true');
      }
    });
    // The segment just left drains (timer) or empties at once (click, swipe) while the one now showing fills from empty.
    if (this.barMode && this.index !== previous) {
      if (auto) this.drainOut(previous);
      else this.clearFill(previous);
      this.fillIn(this.index);
    }
  }

  // Runs a fill animation on one segment, replacing whatever was running there.
  sweep(i, frames, duration, easing) {
    const anim = this.fills[i].animate(frames, { duration, easing, fill: 'forwards' });
    if (this.hasAttribute('data-paused')) anim.pause();
    this.anims[i] = anim;
    return anim;
  }

  clearFill(i) {
    const anim = this.anims[i];
    if (!anim) return;
    anim.onfinish = null;
    anim.cancel();
    this.anims[i] = null;
  }

  // The showing slide's segment fills from empty. With autoplay that is one constant-speed sweep over the interval, and its end
  // is what advances the slide, so the fill and the timer are the same clock and can never drift apart.
  fillIn(i, instant = false) {
    this.clearFill(i);
    const frames = [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }];
    if (this.autoplay) {
      this.sweep(i, frames, this.interval, 'linear').onfinish = () => {
        if (this.index === i) this.go(i + 1, true);
      };
    } else {
      this.sweep(i, frames, instant ? 0 : this.glideMs, this.glideEase);
    }
  }

  // The segment just left empties from the left: its tail travels right to where the fill had reached (the whole segment if it
  // was full), so the white leaves the way it arrived instead of vanishing.
  drainOut(i) {
    const anim = this.anims[i];
    if (!anim || anim.draining) return;
    const filled = new DOMMatrixReadOnly(getComputedStyle(this.fills[i]).transform).a;
    this.clearFill(i);
    if (!this.glideMs || filled < 0.001) return;
    const drain = this.sweep(
      i,
      [{ transform: `translateX(0) scaleX(${filled})` }, { transform: `translateX(${filled * 100}%) scaleX(0)` }],
      this.glideMs,
      this.glideEase
    );
    drain.draining = true;
    drain.onfinish = () => {
      if (this.anims[i] === drain) this.clearFill(i);
    };
  }

  // Slide positions relative to the current one: 0 is in view, -1 sits to the left, 1 to the right.
  // `instant` places every slide without animating (first layout on load).
  layout(instant = false) {
    const count = this.slides.length;
    if (count === 2) {
      // The other slide stays on its side, or takes the side the new slide came from once it has left the view.
      const active = this.index;
      const other = 1 - active;
      const cameFrom = this.positions[active] || 1;
      this.place(active, 0, instant);
      this.place(other, this.positions[other] === 0 ? -Math.sign(cameFrom) : this.positions[other], instant);
      return;
    }
    this.slides.forEach((_, i) => {
      let offset = (i - this.index + count) % count;
      if (offset > count / 2) offset -= count;
      this.place(i, offset, instant);
    });
  }

  // Set one slide's position. A slide that has to jump across the screen (wrapping around the loop) does so without animating.
  place(i, offset, instant = false) {
    const slide = this.slides[i];
    const jumps = instant || Math.abs(offset - this.positions[i]) > 1;
    this.positions[i] = offset;
    if (jumps) {
      slide.setAttribute('data-jump', '');
      slide.style.setProperty('--pos', offset);
      void slide.offsetWidth;
      slide.removeAttribute('data-jump');
    } else {
      slide.style.setProperty('--pos', offset);
    }
  }

  // The timer only stops while a drag is in progress or the tab is hidden; hovering does not pause it.
  hold(on) {
    this.toggleAttribute('data-paused', on);
    // Pause only what is running and resume only what is paused: play() on a finished animation would rewind and replay it.
    this.anims?.forEach((a) => (on ? a?.playState === 'running' && a.pause() : a?.playState === 'paused' && a.play()));
  }
}

if (!customElements.get('hero-slideshow')) customElements.define('hero-slideshow', HeroSlideshow);
